import React, { useState } from "react";
import { Path, useFieldArray, useForm } from "react-hook-form";
import { FormTextControl, RowFormText } from "../Common/Forms/FormText";
import KaAlert from "../Common/KaAlert";
import KaModal from "../Common/KaModal";
import SpinnerButton from "../Common/SpinnerButton";
import useDisplayMessage from "../Common/useDisplayMessage";
import { CarrierDto } from "../../requests/useGetCarriers";
import useUpsertCarrier, { CarrierUpsertDto } from "../../requests/useUpsertCarrier";
import { RowHooksFormTextArea } from "../Common/Forms/FormTextArea";
import { Col, FormGroup } from "reactstrap";
import { FormLabel } from "../Common/Forms/FormLabel";
import BoldButton from "../Common/BoldButton";

type CarrierForm = Omit<CarrierUpsertDto, 'emailAddresses'> & {
  emailAddresses: { email: string }[]
}

interface CreateEditCarrierProps {
  initialCarrier: CarrierDto | null;
  onDismiss: () => void;
  onUpsert: (carrierId: string, carrierName: string, assignSites: boolean) => void;
}

export const CreateEditCarrier = (props: CreateEditCarrierProps) => {
  const displayMessage = useDisplayMessage();
  const { id, ...initialCarrier } = { ...props.initialCarrier };
  const initEmails = initialCarrier.emailAddresses?.map(email => ({ email })) ?? [];
  const { register, handleSubmit, control, getValues, setValue, trigger, formState: { errors } } = useForm<CarrierForm>({
    defaultValues: {
      ...initialCarrier,
      emailAddresses: initEmails.length === 0 ? [{ email: '' }] : initEmails,
    }
  });

  const emailAddresses = useFieldArray({ control, name: 'emailAddresses' });

  const validateUniqueEmail = (value: string) => {
    if (value && getValues('emailAddresses').map(f => f.email).filter(e => e === value).length > 1) {
      return 'Email already exists';
    }
    return true;
  };

  const removeEmail = (index: number) => {
    emailAddresses.remove(index);
    trigger('emailAddresses');
  }

  const [assignSites, setAssignSites] = useState(false);

  const dismissed = () => {
    props.onDismiss();
  };

  const submit = (formValue: CarrierForm, assign: boolean) => {
    setAssignSites(assign);
    upsert.request({
      ...formValue,
      emailAddresses: formValue.emailAddresses.map(e => e.email).filter(e => !!e),
    });
  }

  const hookProps = (name: Path<CarrierForm>) => ({
    labelSpace: 4,
    inputSpace: 7,
    name: name,
    setValue,
    register,
    errors,
  });

  const upsert = useUpsertCarrier(props.initialCarrier?.id, {
    onSuccess: response => {
      props.onDismiss();
      const carrierId = props.initialCarrier ? props.initialCarrier.id : response;
      props.onUpsert(carrierId, getValues().name, assignSites);
    },
    onError: (e) => displayMessage.fail(e.message),
  });

  const labelSpace = 4;
  const inputSpace = 7;

  const emailControl = (index: number) =>
    <FormTextControl
      {...hookProps(`emailAddresses.${index}.email`)}
      displayName='Email address'
      options={{
        onChange: () => trigger('emailAddresses'),
        validate: validateUniqueEmail,
        pattern: {
          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
          message: 'Email Address is invalid'
        }
      }} />

  return <KaModal
    title={`${props.initialCarrier ? 'Update' : 'Create'} a carrier`} onHide={dismissed} show={true}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <RowFormText {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps('number')}
        displayName="Carrier Number"
        options={{
          validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps("addressStreet")}
        displayName="Street"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address Street cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...hookProps("addressCity")}
        displayName="City"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address City cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...hookProps("addressState")}
        displayName="State"
        options={{
          validate: {
            length: v => v.length < 51 || 'Address State cannot be longer than 50 characters'
          }
        }} />
      <RowFormText {...hookProps("addressPostalCode")}
        displayName="Postal Code"
        options={{
          validate: {
            length: v => v.length < 16 || 'Address Postal Code cannot be longer than 15 characters'
          }
        }} />
      <RowFormText {...hookProps("addressCountry")}
        displayName="Country"
        options={{
          validate: {
            length: v => v.length < 101 || 'Address Country cannot be longer than 100 characters'
          }
        }} />
      <RowFormText {...hookProps('phone')}
        options={{
          validate: { length: v => v.length < 51 || 'Phone cannot be longer than 50 characters' }
        }} />
      {
        emailAddresses.fields.map((field, index) => {
          const isFirst = index === 0;
          const showX = emailAddresses.fields.length > 1;
          return <FormGroup row key={field.id}>
            {isFirst &&
              <Col xs={{ size: labelSpace }}>
                <FormLabel displayName='Email' {...hookProps(`emailAddresses.${index}.email`)} />
              </Col>
            }
            <Col xs={{ offset: (isFirst ? 0 : labelSpace), size: inputSpace - (showX ? 1 : 0) }}>
              {emailControl(index)}
            </Col>
            {
              showX &&
              <Col xs="1" className="pe-0">
                <BoldButton className='btn-close btn-sm mt-2' onClick={() => removeEmail(index)} />
              </Col>
            }
          </FormGroup>
        })}
      <FormGroup row>
        <Col xs={{ offset: labelSpace, size: inputSpace }} className='text-end'>
          <BoldButton
            className='col-auto btn-ghost-primary'
            disabled={emailAddresses.fields.length >= 10}
            onClick={() => emailAddresses.append({ email: '' })}>
            Add Email
          </BoldButton>
        </Col>
      </FormGroup>
      <RowHooksFormTextArea {...hookProps("notes")} />
    </>)}
    footer={(
      <div className="btn-group">
        <SpinnerButton
          className="w-25 m-1 rounded btn-ghost-primary"
          spinning={upsert.isLoading && !upsert.isError}
          onClick={handleSubmit(values => submit(values, false))}>
          {`${props.initialCarrier ? 'Update' : 'Create'}`}
        </SpinnerButton>
        <SpinnerButton
          className="w-25 m-1 rounded btn-ghost-primary"
          spinning={upsert.isLoading && !upsert.isError}
          onClick={handleSubmit(values => submit(values, true))}>
          {`${props.initialCarrier ? 'Update and Assign' : 'Create and Assign'}`}
        </SpinnerButton>
      </div>)
    }
  />
};
